import React, { useState } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Send, Megaphone } from 'lucide-react';
import { toast } from 'sonner';

export default function BroadcastPanel() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [sending, setSending] = useState(false);

  const canSend = title.trim() && body.trim() && !sending;

  const send = async () => {
    if (!canSend) return;
    if (!window.confirm('Send this push notification to ALL subscribed devices?')) return;
    setSending(true);
    try {
      const r = await api.post('/admin/broadcast-push', { title: title.trim(), body: body.trim(), url: url.trim() || '/' });
      toast.success(`Push sent to ${r.data.target_devices} device(s).`);
      setTitle(''); setBody(''); setUrl('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send broadcast.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="broadcast-panel">
      <h2 className="font-display font-bold text-2xl text-brand-ink mb-1 flex items-center gap-2"><Megaphone className="w-6 h-6 text-brand" /> Broadcast Push</h2>
      <p className="text-brand-slate text-sm mb-6">Send a custom push notification to every user who has enabled push on a device. Owners only.</p>

      <div className="glass-panel rounded-3xl p-5 sm:p-6 max-w-xl space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Title</Label>
          <Input
            data-testid="broadcast-title"
            value={title} onChange={e => setTitle(e.target.value)} maxLength={80}
            placeholder="e.g., Big neighborhood cleanup this weekend!"
            className="mt-1.5 rounded-xl bg-black/5 border-transparent focus:bg-white focus:border-brand"
          />
          <div className="text-[11px] text-brand-dim mt-1 text-right">{title.length}/80</div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Message</Label>
          <Textarea
            data-testid="broadcast-body"
            value={body} onChange={e => setBody(e.target.value)} maxLength={300} rows={3}
            placeholder="Write the notification message neighbors will see…"
            className="mt-1.5 rounded-xl bg-black/5 border-transparent focus:bg-white focus:border-brand resize-none"
          />
          <div className="text-[11px] text-brand-dim mt-1 text-right">{body.length}/300</div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Link (optional)</Label>
          <Input
            data-testid="broadcast-url"
            value={url} onChange={e => setUrl(e.target.value)}
            placeholder="/ (defaults to the map)"
            className="mt-1.5 rounded-xl bg-black/5 border-transparent focus:bg-white focus:border-brand"
          />
        </div>
        <div className="flex justify-end pt-1">
          <Button
            data-testid="broadcast-send-btn"
            onClick={send} disabled={!canSend}
            className="rounded-full bg-brand hover:bg-brand-hover text-white px-6"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Send to all
          </Button>
        </div>
      </div>
    </div>
  );
}
